import { uuidv7 } from '@core/ids';
import { last4 } from '@core/phone';

import { acceptSession, api, ApiError, dropSession, publicApi, refreshSession, setSignedOutHandler } from './api';
import {
  applyPull,
  emptyState,
  markPushed,
  stageCategory,
  stageExpense,
  type DataState,
} from './merge';
import { DEFAULT_CATEGORIES, type Category, type Expense, type PullPage, type User } from './types';

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error';

export type AppState = {
  auth: 'loading' | 'signedOut' | 'signedIn';
  user: User | null;
  data: DataState;
  /** First full pull finished — screens can render real numbers. */
  loaded: boolean;
  sync: SyncState;
  syncMessage: string | null;
  lastSyncAt: string | null;
};

let state: AppState = {
  auth: 'loading',
  user: null,
  data: emptyState(),
  loaded: false,
  sync: 'idle',
  syncMessage: null,
  lastSyncAt: null,
};
const listeners = new Set<() => void>();

function set(patch: Partial<AppState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export const store = {
  get: () => state,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

setSignedOutHandler(() => resetToSignedOut());

function resetToSignedOut() {
  dropSession();
  localStorage.removeItem(SESSION_HINT);
  set({ auth: 'signedOut', user: null, data: emptyState(), loaded: false, lastSyncAt: null });
}

// ── Session ─────────────────────────────────────────────────────────────────

type SignInResponse = { user: User; needsProfile: boolean; accessToken: string; accessTokenExpiresIn: number };

/** On page load: the refresh cookie (if any) brings the session back. */
const SESSION_HINT = 'spendly.hasSession'; // not a secret: only "try the refresh cookie"
/** Last 4 digits of the last signed-in number — the login "previously used"
 *  hint. Deliberately NOT cleared on sign-out so it survives. Not a secret. */
const LAST_PHONE_LAST4 = 'spendly.lastPhoneLast4';

export async function restore(): Promise<void> {
  if (!localStorage.getItem(SESSION_HINT)) return resetToSignedOut();
  try {
    if (!(await refreshSession())) return resetToSignedOut();
    await afterSignIn(await api<User>('GET', '/me'));
  } catch {
    // Server unreachable: show sign-in; the user can retry.
    resetToSignedOut();
  }
}

export async function pinStatus(phone: string) {
  return (await publicApi<{ status: 'new' | 'existing' }>('POST', '/auth/pin/status', { phone })).status;
}

export async function signIn(phone: string, pin: string, isNew: boolean): Promise<{ needsProfile: boolean }> {
  const res = await publicApi<SignInResponse>('POST', isNew ? '/auth/pin/register' : '/auth/pin/login', {
    phone,
    pin,
  });
  acceptSession(res);
  await afterSignIn(res.user);
  return { needsProfile: res.needsProfile };
}

async function afterSignIn(user: User) {
  localStorage.setItem(SESSION_HINT, '1');
  const hint = last4(user.phone);
  if (hint) localStorage.setItem(LAST_PHONE_LAST4, hint);
  set({ auth: 'signedIn', user, data: emptyState(), loaded: false });
  await syncNow();
}

export function lastPhoneLast4(): string | null {
  return localStorage.getItem(LAST_PHONE_LAST4);
}

export async function signOut() {
  await publicApi('POST', '/auth/logout').catch(() => {});
  resetToSignedOut();
}

export async function updateProfile(patch: Partial<Pick<User, 'firstName' | 'lastName' | 'currency'>>) {
  set({ user: await api<User>('PATCH', '/me', patch) });
}

export async function changePin(currentPin: string, newPin: string) {
  await api('POST', '/me/pin', { currentPin, newPin });
}

export async function deleteAccount() {
  await api('DELETE', '/me');
  resetToSignedOut();
}

// ── Sync ────────────────────────────────────────────────────────────────────

let running: Promise<void> | null = null;
let again = false;
let pushTimer: ReturnType<typeof setTimeout> | null = null;

/** push → pull until caught up. Coalesced: concurrent calls share one run. */
export function syncNow(): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    set({ sync: 'syncing', syncMessage: null });
    try {
      do {
        again = false;
        await push();
        await pullAll();
      } while (again);
      await seedDefaultsIfEmpty();
      set({ sync: 'idle', loaded: true, lastSyncAt: new Date().toISOString() });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      const offline = e instanceof ApiError && e.status === 0;
      set({
        sync: offline ? 'offline' : 'error',
        syncMessage: offline ? "Offline — changes will sync when you're back." : (e as Error).message,
        loaded: state.loaded || state.data.expenses.size > 0,
      });
    } finally {
      running = null;
    }
  })();
  return running;
}

async function push() {
  const categories = [...state.data.pendingCategories.values()];
  const expenses = [...state.data.pendingExpenses.values()];
  for (let i = 0; i < categories.length + expenses.length; i += 400) {
    const batch = [...categories.map((c) => ({ k: 'c' as const, c })), ...expenses.map((e) => ({ k: 'e' as const, e }))].slice(i, i + 400);
    const body = {
      categories: batch.flatMap((b) => (b.k === 'c' ? [b.c] : [])),
      expenses: batch.flatMap((b) => (b.k === 'e' ? [b.e] : [])),
    };
    await api('POST', '/sync/push', body);
    set({ data: markPushed(state.data, body) });
  }
}

async function pullAll() {
  for (;;) {
    const page = await api<PullPage>('GET', `/sync/pull?since=${state.data.cursor}&limit=500`);
    set({ data: applyPull(state.data, page) });
    if (!page.hasMore) return;
  }
}

/** Brand-new web-first account: same default categories (same ids) the phone seeds. */
async function seedDefaultsIfEmpty() {
  if (state.data.categories.size > 0) return;
  const now = new Date().toISOString();
  let data = state.data;
  DEFAULT_CATEGORIES.forEach((c, i) => {
    data = stageCategory(data, {
      ...c,
      parentId: null,
      sortOrder: i,
      isDefault: true,
      isArchived: false,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    });
  });
  set({ data });
  await push();
}

function schedulePush() {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => void syncNow(), 400);
}

/** Keep fresh while the tab is open: on focus and every minute. */
export function startBackgroundSync(): () => void {
  const onFocus = () => state.auth === 'signedIn' && void syncNow();
  window.addEventListener('focus', onFocus);
  const timer = setInterval(onFocus, 60_000);
  return () => {
    window.removeEventListener('focus', onFocus);
    clearInterval(timer);
  };
}

// ── Edits ───────────────────────────────────────────────────────────────────

export type ExpenseDraft = Omit<Expense, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>;

/** ISO timestamp strictly after `prev`, so last-write-wins always sees a newer edit. */
function nextStamp(prev?: string): string {
  const now = new Date();
  if (prev && now.toISOString() <= prev) return new Date(Date.parse(prev) + 1).toISOString();
  return now.toISOString();
}

export function saveExpense(draft: ExpenseDraft, id?: string): Expense {
  const existing = id ? state.data.expenses.get(id) : undefined;
  const now = nextStamp(existing?.updatedAt);
  const expense: Expense = {
    ...draft,
    id: existing?.id ?? uuidv7(),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    deletedAt: null,
  };
  set({ data: stageExpense(state.data, expense) });
  schedulePush();
  return expense;
}

export function setExpenseDeleted(id: string, deleted: boolean) {
  const e = state.data.expenses.get(id);
  if (!e) return;
  const now = nextStamp(e.updatedAt);
  set({ data: stageExpense(state.data, { ...e, deletedAt: deleted ? now : null, updatedAt: now }) });
  schedulePush();
}

export function saveCategory(patch: Pick<Category, 'name' | 'icon' | 'color'>, id?: string) {
  const existing = id ? state.data.categories.get(id) : undefined;
  const now = nextStamp(existing?.updatedAt);
  const maxOrder = Math.max(-1, ...[...state.data.categories.values()].map((c) => c.sortOrder));
  const category: Category = existing
    ? { ...existing, ...patch, updatedAt: now }
    : {
        id: uuidv7(),
        parentId: null,
        ...patch,
        sortOrder: maxOrder + 1,
        isDefault: false,
        isArchived: false,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      };
  set({ data: stageCategory(state.data, category) });
  schedulePush();
}

export function setCategoryArchived(id: string, archived: boolean) {
  const c = state.data.categories.get(id);
  if (!c) return;
  set({ data: stageCategory(state.data, { ...c, isArchived: archived, updatedAt: nextStamp(c.updatedAt) }) });
  schedulePush();
}

/** Moves every expense (incl. deleted) to `toId`, then tombstones the category. */
export function deleteCategory(id: string, toId: string | null) {
  let data = state.data;
  for (const e of data.expenses.values()) {
    if (e.categoryId !== id) continue;
    if (!toId) throw new Error('Choose a category to move expenses to');
    data = stageExpense(data, { ...e, categoryId: toId, updatedAt: nextStamp(e.updatedAt) });
  }
  const c = data.categories.get(id);
  if (c) {
    const now = nextStamp(c.updatedAt);
    data = stageCategory(data, { ...c, deletedAt: now, updatedAt: now });
  }
  set({ data });
  schedulePush();
}

// ── Receipts ────────────────────────────────────────────────────────────────

/** Re-encodes to WebP (longest side ≤ 1600px) in the browser before upload, like the app. */
export async function compressImage(file: File): Promise<{ blob: Blob; width: number; height: number }> {
  if (!file.type.startsWith('image/')) throw new Error('Please choose an image file.');
  if (file.size > 25 * 1024 * 1024) throw new Error('That image is too large.');
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', 0.8));
  if (!blob) throw new Error("Couldn't process that image.");
  return { blob, width, height };
}

export async function uploadReceipt(expenseId: string, file: File) {
  // The server needs the expense first.
  await syncNow();
  const { blob, width, height } = await compressImage(file);
  const form = new FormData();
  form.append('id', uuidv7());
  form.append('width', String(width));
  form.append('height', String(height));
  form.append('file', blob, 'receipt.webp');
  await api('PUT', `/receipts/${encodeURIComponent(expenseId)}`, form);
  await syncNow();
}

export async function removeReceipt(expenseId: string) {
  await api('DELETE', `/receipts/${encodeURIComponent(expenseId)}`);
  await syncNow();
}
